// ConfirmDialog — `window.confirm()` 置き換え汎用モーダル (2026-05-21).
//
// 背景: iPad Safari ではネイティブ `confirm()` を出すと **全画面モードが解除される**
// 問題があり、毎回手動で再全画面化する UX 上のストレスがある (= 2026-05-21
// ユーザー指摘). モーダル化することで全画面のまま yes/no 確認を取れる.
//
// API: controlled component. 親が `open` boolean を持ち、`onConfirm` / `onCancel`
// で結果を受ける. ネイティブ `confirm()` と同じ感覚で使えるよう、本コンポーネント
// は内部 state を持たない (= UserPreferencesModal / RoomSettingsModal と同流儀).
//
// a11y: `role="dialog"` + `aria-modal="true"` + Esc キー + backdrop click で閉じる.
// 既存モーダルの慣習に合わせ、open 時に **キャンセルボタン** にフォーカスを移す
// (= 破壊的な OK を誤発火させないため. 「Enter で即実行」は意図しない hit になる).
//
// z-index: 既存 modal (`z-50`) の上に乗せる必要がある (= RoomSettingsModal 内の
// credential 削除確認のような **ネスト confirm** ケース) ため `z-[60]` を当てる.
//
// 配色: `destructive=true` 時の確認ボタンは AdminPage の destructive 削除ボタン
// (= `AdminPage.tsx:432` / `RoomSettingsModal.tsx` `CredentialRow` 削除ボタン) と
// 同じ `bg-[var(--color-error)]` 系で揃え、サービス全体の visual consistency を保つ.
//
// メッセージは `\n` 改行を `whitespace-pre-wrap` で表示するので、呼び出し側は
// 既存 `window.confirm()` の改行文字列をそのまま流せる.
//
// --- タッチ/ペンでのゴーストクリック対策 (2026-07-25 staging 実機再現・原因特定済み) ---
//
// 症状: Classic「初期色に戻す」ボタンを**タッチでタップ**すると、確認ダイアログが
// 一瞬開いて即座に閉じる (ユーザーには「何も起きない」ように見える).
//
// 採取したイベントトレース:
//   pointerup(touch)→BUTTON[初期色に戻す]   ← useTapActivate が pointerup で発火 → ダイアログが開く
//   dialog=OPEN
//   click→BUTTON[閉じる]                     ← touch 由来の互換 click が backdrop に着弾
//   dialog=closed
//
// 機序: `useTapActivate` はペン/Wacom 対策で **pointerup** 起動する (`useTapActivate.ts`
// 冒頭コメント参照). その pointerup の時点で `ConfirmDialogView` が `fixed inset-0` の
// backdrop (`aria-label="閉じる"` の button) を全面に描画する. その直後に発火する
// **touch 由来の互換 click は、ディスパッチ時点で改めてヒットテストされる**ため、
// 元のボタンではなく**新しく現れた backdrop に着弾**し `onCancel` が走ってしまう.
// マウスは click の対象が mousedown 時点の要素に固定される (W3C UI Events の
// "click 対象は pointerdown/mousedown で決まる" 挙動) ため発生しない — 実機で
// touch/pen でのみ再現しマウスでは再現しないのはこのため.
//
// 影響範囲: `ConfirmDialog` を開くすべての経路 (Classic/Modern 双方の
// `useTapActivate` 系ボタンから開くケース全般). backdrop に着弾すれば「開かない」
// だけで済むが、タップ位置がダイアログの「確定」ボタンの位置と重なった場合は
// **ユーザーが認識しないまま破壊的操作 (全消し・kick 等) が実行される**危険がある.
//
// 対策 (本ファイルに一元化. 呼び出し側 20+ 箇所は無改修で済む):
//   - `open` が false→true になったら「未 arm」にリセットする.
//   - ダイアログ内 (backdrop / 本体どちらでも) の `onPointerDown` で arm する.
//   - `onConfirm` / `onCancel` / backdrop click は **arm 済みのときだけ** 実行する.
//   - 例外: キーボード (Enter/Space) 由来の click は常に通す. 判定は
//     `event.detail === 0` (Enter/Space 由来の click は `detail` が 0, pointer/touch
//     由来の click は 1 以上 — UI Events 仕様上の既定挙動). これが無いとキーボード
//     操作でダイアログを閉じられなくなる (open 直後は未 arm のため).
//   - ESC キーによるクローズは本ガードの対象外 (常に許可、既存のまま).
//
// このガードにより「開いた瞬間に、開く前から進行中だった touch 由来の click が
// 新出現の backdrop/ボタンに誤着弾する」パターンを遮断しつつ、「開いた後に改めて
// 指/ペンでボタンを押す」正規操作はそのまま通る (pointerdown が dialog 内で
// 起きているため arm 済み).

import { useEffect, useId, useRef } from 'react';

export interface ConfirmDialogProps {
  /** open 状態 (controlled). false なら DOM に何も出さない. */
  readonly open: boolean;
  /** ダイアログのタイトル. 短く. */
  readonly title: string;
  /** 本文. `\n` 改行は `whitespace-pre-wrap` で改行表示される. */
  readonly message: string;
  /** 確認ボタンラベル. default 「OK」. */
  readonly confirmLabel?: string;
  /** キャンセルボタンラベル. default 「キャンセル」. */
  readonly cancelLabel?: string;
  /** true で確認ボタンを赤系 (= destructive) 配色に. default false. */
  readonly destructive?: boolean;
  /** 確認ボタン click 時. */
  readonly onConfirm: () => void;
  /** キャンセル / backdrop / Esc 時. */
  readonly onCancel: () => void;
}

/**
 * ゴーストクリック対策の許可判定 (ファイル冒頭コメント参照).
 *
 * `armed` (= ダイアログ表示中に一度でも内部で pointerdown があったか) が true か、
 * `detail === 0` (= キーボード Enter/Space 由来の click) のどちらかを満たすときだけ
 * click を通す. pointer/touch 由来の click は `detail >= 1` になる (UI Events 仕様の
 * 既定挙動) ため、「ダイアログが開く前から進行中だった touch 由来の click が新出現の
 * backdrop/ボタンに誤着弾するケース」(= `armed=false` かつ `detail>=1`) だけを弾ける.
 */
export const shouldAllowConfirmDialogClick = (armed: boolean, detail: number): boolean =>
  armed || detail === 0;

/**
 * `armedRef` を open の false→true 遷移時にリセットする、`ConfirmDialog` の render
 * 本体から**毎 render 呼ぶ**同期関数 (レビュー指摘 2026-07-25 対応).
 *
 * なぜ `useEffect` ではなくこの形 (render 中の同期呼び出し) にしているか:
 * `useEffect` (passive effect) は **commit・paint 後に非同期で** 走るのに対し、touch の
 * ゴーストクリックは pointerup とほぼ同一タスク内で連続してディスパッチされる (実測
 * トレースでも pointerup → DOM 変化 → click が同一タスクで連続). そのため
 * `useEffect(() => { if (open) armedRef.current = false }, [open])` のような実装だと、
 * 「前回の操作で armed=true のまま閉じたダイアログを、すぐさま再オープンした直後
 * (= passive effect がまだ走っていないタイミング) にゴーストクリックが着弾する」
 * ケースで古い `armed=true` が残ったままガードを素通りしてしまう
 * (= 初回だけ直り、2 回目以降に間欠的に再発するという厄介なレース).
 *
 * 本関数は `ConfirmDialog` の関数本体で (hooks ではなく) 毎 render 呼ばれる、副作用を
 * 起こさない ref の同期書き換えのみなので、Strict Mode の 2 回 render でも安全
 * (= React 公式ドキュメントが認める「render 中に前回値と比較して ref/state を調整
 * する」パターン). **`useEffect` に戻す変更を入れないこと** — 上記のレースが再発する.
 *
 * pure 関数として切り出したことで、DOM / React の commit・effect タイミングに依存
 * せず「render 呼び出しそのものが同期的にリセットしているか」を直接テストできる
 * (`ConfirmDialog.test.tsx` 参照).
 */
export const resetArmedOnReopen = (
  armedRef: { current: boolean },
  prevOpenRef: { current: boolean },
  open: boolean,
): void => {
  if (open && !prevOpenRef.current) {
    armedRef.current = false;
  }
  prevOpenRef.current = open;
};

/**
 * Pure UI 部分 (hooks 最小限). props ですべてが決まる function なので
 * テストでは `ConfirmDialogView({...})` を直接呼んで React tree を走査できる
 * (= UserPreferencesModalView と同流儀).
 *
 * 外側の `ConfirmDialog` で `useId` / `useEffect` (Esc listener + focus) / `useRef`
 * (arm 状態) を捌き、ID と autoFocusRef / armedRef を本コンポーネントに props で渡す.
 */
export interface ConfirmDialogViewProps extends ConfirmDialogProps {
  /** Dialog 見出しの aria-labelledby 用 ID. */
  readonly titleId: string;
  /** 本文 paragraph の aria-describedby 用 ID. */
  readonly messageId: string;
  /**
   * キャンセルボタンに刺す ref (open 時 autoFocus 用). テストでは省略可
   * (= ConfirmDialogView を直接呼ぶ pure 関数テストでは ref 不要).
   */
  readonly cancelButtonRef?: React.Ref<HTMLButtonElement>;
  /**
   * ゴーストクリック対策の arm 状態 (ファイル冒頭コメント参照). `current` を
   * `onPointerDown` で true にし、click ハンドラで読む. state ではなく ref にして
   * いるのは、pointerdown → click という同一操作内での参照を re-render のタイミング
   * に依存させないため (実機では 2 イベントが極めて近接して発火する).
   */
  readonly armedRef: { current: boolean };
}

export const ConfirmDialogView = ({
  open,
  title,
  message,
  confirmLabel = 'OK',
  cancelLabel = 'キャンセル',
  destructive = false,
  onConfirm,
  onCancel,
  titleId,
  messageId,
  cancelButtonRef,
  armedRef,
}: ConfirmDialogViewProps): React.ReactNode => {
  if (!open) return null;
  // 確認ボタンの配色: destructive=true で赤系 (= AdminPage 削除ボタンと visual
  // consistency). default は accent 色 (= 通常の primary action).
  const confirmClassName = destructive
    ? 'rounded bg-[var(--color-error)] px-4 py-2 text-sm font-bold text-white transition hover:brightness-110'
    : 'rounded bg-[var(--color-accent)] px-4 py-2 text-sm font-bold text-[var(--color-panel)] transition hover:brightness-110';
  // ゴーストクリック対策 (ファイル冒頭コメント参照): arm 済み、または detail===0
  // (キーボード由来) のときだけ実行する. backdrop / キャンセル / 確認の 3 箇所に
  // 同じガードをかける (= 破壊的操作の誤爆リスクがある確認ボタンも例外なし).
  const handleCancel = (e: { detail: number }): void => {
    if (shouldAllowConfirmDialogClick(armedRef.current, e.detail)) onCancel();
  };
  const handleConfirm = (e: { detail: number }): void => {
    if (shouldAllowConfirmDialogClick(armedRef.current, e.detail)) onConfirm();
  };
  // onPointerDown: ダイアログ内 (backdrop / 本体どちらでも) の押下で arm する.
  // outer wrapper で受けてバブリングに任せる (backdrop / dialog 本体を個別に
  // ハンドリングしない).
  const handlePointerDown = (): void => {
    armedRef.current = true;
  };
  return (
    // z-[60]: 既存 modal (z-50) の上に重ねるため (RoomSettingsModal 内ネスト用).
    // onPointerDown: ゴーストクリック対策の arm (ファイル冒頭コメント参照)。
    <div
      className="fixed inset-0 z-[60] grid place-items-center p-4"
      onPointerDown={handlePointerDown}
    >
      {/* Backdrop. UserPreferencesModal と同流儀で a11y compliant な button.
          tabIndex=-1 で tab フォーカスは取らないが、click + Esc 経由で閉じる. */}
      <button
        type="button"
        aria-label="閉じる"
        tabIndex={-1}
        onClick={handleCancel}
        className="-z-10 absolute inset-0 cursor-default bg-black/60"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        // w-full max-w + min-w-0: 固定 w-[400px] はスマホ縦 (390/360px) で親 p-4 を
        // 貫通してはみ出す (2026-07-03 一括点検)。min-w-0 は grid 子の intrinsic min
        // (長い無改行 message) による max-w 貫通を防ぐ保険。
        className="max-h-[90vh] w-full min-w-0 max-w-[400px] overflow-y-auto rounded border border-white/10 bg-[var(--color-panel)] p-6 shadow-xl"
      >
        <h2 id={titleId} className="mb-3 text-base font-bold text-[var(--color-accent)]">
          {title}
        </h2>
        {/* whitespace-pre-wrap: 呼び出し側が `\n` を含むメッセージを渡せるように
            (= 旧 `window.confirm()` 改行文字列との互換). */}
        <p
          id={messageId}
          className="mb-5 break-words whitespace-pre-wrap text-sm text-[var(--color-text)]"
        >
          {message}
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            ref={cancelButtonRef}
            onClick={handleCancel}
            className="rounded border border-white/30 bg-[var(--color-bg-soft)] px-4 py-2 text-sm text-white hover:bg-[var(--color-bg)]"
          >
            {cancelLabel}
          </button>
          <button type="button" onClick={handleConfirm} className={confirmClassName}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export const ConfirmDialog = (props: ConfirmDialogProps): React.ReactNode => {
  const titleId = useId();
  const messageId = useId();
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
  // ゴーストクリック対策の arm 状態 (ファイル冒頭コメント参照). ref にして
  // pointerdown→click の参照を re-render タイミングに依存させない.
  const armedRef = useRef(false);
  const prevOpenRef = useRef(props.open);

  // open が false→true になった瞬間、render 中に同期で armedRef をリセットする.
  // なぜ useEffect ではなくこの形なのか、詳細は `resetArmedOnReopen` の doc comment
  // を参照 (レビュー指摘 2026-07-25 対応. **useEffect に戻す変更を入れないこと**).
  resetArmedOnReopen(armedRef, prevOpenRef, props.open);

  // Esc で閉じる (UserPreferencesModal / RoomSettingsModal と同パターン).
  // `open=false` の間は listener を attach しない (= 多段 modal 時に他 modal の
  // Esc を奪わない). ガード (ファイル冒頭コメント) の対象外 — Esc は常に許可する.
  const { onCancel, open } = props;
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [onCancel, open]);

  // open 遷移時にキャンセルボタンへフォーカス. 破壊的 OK を誤 hit させないため
  // (= 開いた瞬間の Enter / Space で確認ボタンが押される事故を避ける).
  useEffect(() => {
    if (!open) return;
    cancelButtonRef.current?.focus();
  }, [open]);

  return (
    <ConfirmDialogView
      {...props}
      titleId={titleId}
      messageId={messageId}
      cancelButtonRef={cancelButtonRef}
      armedRef={armedRef}
    />
  );
};
